import { useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, BookOpen, Users, Crown, Sparkles } from "lucide-react";
import { manuale, MANUALE_VERSIONE, MANUALE_DATA } from "@/data/manuale";

export default function Manuale() {
  const { settings, showcase } = useApp();
  const { lang } = useI18n();
  const m = manuale(lang);
  const [chi, setChi] = useState("personale");
  const secs = chi === "titolare" ? m.titolare : m.personale;
  const titolo = chi === "titolare" ? m.ownerTitle : m.staffTitle;

  const stampa = () => {
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) return toast.error(m.popups);
    const body = secs
      .map(
        (s, i) => `<h2 style="margin:22px 0 6px;font-size:15px">${i + 1}. ${s.t}</h2>
        <ul style="margin:0;padding-left:18px">${s.p.map((x) => `<li style="margin-bottom:5px">${x}</li>`).join("")}</ul>`,
      )
      .join("");
    const indice = secs.map((s, i) => `<li>${i + 1}. ${s.t}</li>`).join("");
    w.document.write(`<html><head><title>${titolo}</title></head>
      <body style="font-family:Helvetica,Arial,sans-serif;font-size:12px;line-height:1.5;color:#0f172a;padding:28px;max-width:720px;margin:auto">
      ${settings.logo ? `<p style="text-align:center"><img src="${settings.logo}" style="max-height:64px"/></p>` : ""}
      <h1 style="text-align:center;font-size:22px;margin:6px 0">${settings.business_name || "Reprehendo"}</h1>
      ${settings.motto ? `<p style="text-align:center;font-style:italic;margin:0">«${settings.motto}»</p>` : ""}
      <p style="text-align:center;margin:10px 0 2px;font-size:15px;font-weight:bold">${titolo}</p>
      <p style="text-align:center;color:#64748b;margin:0">${m.updated(MANUALE_VERSIONE, MANUALE_DATA)}</p>
      <h2 style="margin:22px 0 6px;font-size:15px">${m.news}</h2>
      <ul style="margin:0;padding-left:18px">${m.novita.map((n) => `<li style="margin-bottom:4px">${n}</li>`).join("")}</ul>
      <h2 style="margin:22px 0 6px;font-size:15px">${m.index}</h2>
      <ol style="margin:0;padding-left:18px;list-style:none">${indice}</ol>
      ${body}
      <p style="margin-top:26px;color:#64748b;font-size:10px;text-align:center">${m.footer}</p>
      </body></html>`);
    w.document.close();
    w.print();
  };

  // Il manuale completo (con versioni e novità) resta a chi si registra: in vetrina non si apre.
  if (showcase)
    return (
      <div className="fade-up max-w-xl space-y-4" data-testid="manuale-riservato">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{m.titolo}</h1>
        <Card className="space-y-3 p-6">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <BookOpen className="h-4 w-4 text-amber-500" /> Il manuale completo è per le attività registrate
          </p>
          <p className="text-sm text-slate-600">
            Dentro ci sono tutte le funzioni spiegate una per una e l'elenco delle novità di ogni
            versione: lo trovi appena apri la tua attività, gratis e senza carta di credito.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button className="h-12" onClick={() => { window.location.href = "/prova"; }} data-testid="manuale-prova-btn">
              <Sparkles className="mr-1.5 h-4 w-4" /> Apri la tua demo in 60 secondi
            </Button>
            <Button variant="outline" className="h-12" onClick={() => { window.location.href = "/guida"; }} data-testid="manuale-guida-btn">
              Vedi la guida rapida
            </Button>
          </div>
        </Card>
      </div>
    );

  return (
    <div className="fade-up max-w-3xl space-y-5" data-testid="manuale-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{m.overline}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{m.titolo}</h1>
        <p className="mt-1 text-sm text-slate-500">{m.version(MANUALE_VERSIONE, MANUALE_DATA)}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant={chi === "personale" ? "default" : "outline"}
          onClick={() => setChi("personale")}
          data-testid="manuale-tab-personale"
        >
          <Users className="mr-1.5 h-4 w-4" /> {m.staffTab}
        </Button>
        <Button
          variant={chi === "titolare" ? "default" : "outline"}
          onClick={() => setChi("titolare")}
          data-testid="manuale-tab-titolare"
        >
          <Crown className="mr-1.5 h-4 w-4" /> {m.ownerTab}
        </Button>
        <Button variant="outline" onClick={stampa} data-testid="manuale-print-btn">
          <Printer className="mr-1.5 h-4 w-4" /> {m.print}
        </Button>
      </div>

      <Card className="border-2 border-amber-300 bg-amber-50/70 p-5" data-testid="manuale-novita">
        <h2 className="flex items-center gap-2 font-head text-base font-bold">
          <Sparkles className="h-4 w-4 text-amber-600" /> {m.news}
        </h2>
        <ul className="mt-2 space-y-1 text-sm text-slate-700">
          {m.novita.map((n) => (
            <li key={n}>· {n}</li>
          ))}
        </ul>
      </Card>

      <div className="space-y-4">
        {secs.map((s, i) => (
          <Card key={s.t} className="p-5" data-testid={`manuale-sezione-${i + 1}`}>
            <h2 className="flex items-start gap-2 font-head text-base font-bold md:text-lg">
              <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <span>
                {i + 1}. {s.t}
              </span>
            </h2>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
              {s.p.map((x) => (
                <li key={x} className="flex gap-2">
                  <span className="text-slate-300">·</span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  );
}
